import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  FormLabel,
  Option,
  Select,
  Sheet,
  Stack,
  Table,
  Typography,
} from '@mui/joy';
import { useEffect, useState } from 'react';
import { listAuditEvents } from '../api/mfaAdmin.api';
import {
  ACTION_COLORS,
  ACTION_LABELS,
  METHOD_LABELS,
} from '../labels/mfaAdmin.labels';
import type { AuditEvent, MfaAuditAction } from '../types/mfaAdmin.types';

const displayName = (
  actor: AuditEvent['actor'] | AuditEvent['target']
): string => {
  const parts = [actor.firstName, actor.lastName].filter(Boolean);
  const name = parts.join(' ');
  return name ? `${name} (${actor.email})` : actor.email;
};

// Cursor-paginated audit view. Each page grabs 25 rows via GET /mfa/admin/audit
// and appends to the visible list; "Meer laden" fetches the next page using
// the last-row _id as the cursor. Filter changes reset the cursor.

const AuditLogAdminPage = () => {
  const [rows, setRows] = useState<AuditEvent[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionFilter, setActionFilter] = useState<MfaAuditAction | ''>('');

  const load = async (opts: { reset: boolean }) => {
    setLoading(true);
    setError(null);
    try {
      const query: {
        cursor?: string;
        action?: MfaAuditAction;
        limit: number;
      } = { limit: 25 };
      if (!opts.reset && cursor) query.cursor = cursor;
      if (actionFilter) query.action = actionFilter;
      const result = await listAuditEvents(query);
      setRows((prev) =>
        opts.reset ? result.events : [...prev, ...result.events]
      );
      setCursor(result.nextCursor);
      setHasMore(Boolean(result.nextCursor));
    } catch (err: any) {
      setError(
        err?.response?.data?.message ??
          'Kon het auditlog niet laden. Probeer opnieuw.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Filter changes are the only reason to reload here. `load` closes over
    // component state that's reset in the same tick, so omitting it from
    // deps is correct for the semantics we want.
    setRows([]);
    setCursor(null);
    setHasMore(true);
    load({ reset: true });
    // eslint-disable-next-line
  }, [actionFilter]);

  return (
    <Box>
      <Typography level="h3" sx={{ mb: 2 }}>
        Auditlog
      </Typography>

      {error && (
        <Alert color="danger" variant="soft" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <FormControl sx={{ minWidth: 240 }}>
          <FormLabel>Filter op actie</FormLabel>
          <Select
            value={actionFilter}
            onChange={(_, value) =>
              setActionFilter((value as MfaAuditAction | '') ?? '')
            }
          >
            <Option value="">Alle acties</Option>
            {(Object.keys(ACTION_LABELS) as MfaAuditAction[]).map((a) => (
              <Option key={a} value={a}>
                {ACTION_LABELS[a]}
              </Option>
            ))}
          </Select>
        </FormControl>
      </Stack>

      <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
        {/*
          Widths sum to ~1140px and Table has minWidth to match so the sum
          holds even in a narrow container — the Sheet's overflow:auto then
          gives horizontal scroll when the browser window is narrower. Long
          user emails use overflowWrap so they wrap inside their cell
          instead of pushing into the next column.
        */}
        <Table stickyHeader sx={{ minWidth: 1140, tableLayout: 'fixed' }}>
          <thead>
            <tr>
              <th style={{ width: 150, whiteSpace: 'nowrap' }}>Wanneer</th>
              <th style={{ width: 170, whiteSpace: 'nowrap' }}>Actie</th>
              <th style={{ width: 260 }}>Doelgebruiker</th>
              <th style={{ width: 260 }}>Uitgevoerd door</th>
              <th style={{ width: 180 }}>Verificatie</th>
              <th style={{ width: 320 }}>Notities</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr>
                <td colSpan={6}>
                  <Typography level="body-sm">
                    Nog geen auditrijen voor deze filter.
                  </Typography>
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r._id}>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {new Date(r.createdAt).toLocaleString('nl-NL')}
                </td>
                <td>
                  <Chip
                    size="sm"
                    variant="soft"
                    color={ACTION_COLORS[r.action]}
                  >
                    {ACTION_LABELS[r.action]}
                  </Chip>
                </td>
                <td style={{ overflowWrap: 'anywhere' }}>
                  {displayName(r.target)}
                </td>
                <td style={{ overflowWrap: 'anywhere' }}>
                  {displayName(r.actor)}
                </td>
                <td>
                  {r.verificationMethod
                    ? METHOD_LABELS[r.verificationMethod]
                    : '-'}
                </td>
                <td style={{ overflowWrap: 'anywhere' }}>
                  {r.notes ?? '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Sheet>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <Button
          variant="soft"
          onClick={() => load({ reset: false })}
          loading={loading}
          disabled={!hasMore}
        >
          {hasMore ? 'Meer laden' : 'Einde van het log'}
        </Button>
      </Box>
    </Box>
  );
};

export default AuditLogAdminPage;
